import { useEffect, useState, useRef } from "react";

/**
 * Auto-hide header behaviour: returns `true` when the header should be
 * visible, `false` when it should be hidden.
 *
 * Rules:
 *   - Always visible near the top of the page (< topThreshold).
 *   - Hidden when the user scrolls DOWN past the threshold.
 *   - Re-shown as soon as the user scrolls UP.
 *
 * Small idle guard (idleDelta) prevents flicker on tiny wheel/scroll deltas.
 */
export default function useHideOnScrollDown({
  topThreshold = 80,
  idleDelta = 6,
} = {}) {
  const [visible, setVisible] = useState(true);
  const lastY = useRef(typeof window === "undefined" ? 0 : window.scrollY);
  const ticking = useRef(false);

  useEffect(() => {
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(() => {
        const y = window.scrollY;
        const delta = y - lastY.current;

        if (y < topThreshold) {
          setVisible(true);
        } else if (Math.abs(delta) > idleDelta) {
          setVisible(delta < 0); // scrolling up → show
        }

        lastY.current = y;
        ticking.current = false;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [topThreshold, idleDelta]);

  return visible;
}
